/**
 * Recall: the jury-facing narrative page (route `/`).
 *
 * A document, not an application. It states the thesis, shows the problem with
 * captured dates, explains the fleet and the authority boundary, maps the
 * competition's platform capabilities to named mechanisms with honest
 * verification badges, and hands the reader to the evidence surface at
 * `#/demo`.
 *
 * Discipline carried over from the shipped surfaces: missing data never renders
 * as zero or as success; a replay never renders as live; HALTED is never shown
 * as ABSTAIN; colour is always redundant with words; and every number is either
 * stamped with the instant it was read, named as a frozen measurement with its
 * own commit, or shown as PENDING.
 */

import './site.css';

import categoryFit from './data/category-fit.json';
import liveRun from './data/live-run.json';
import historicalCase from '../data/historical-case.json';

type BadgeKind = 'LIVE VERIFIED' | 'SOURCE VERIFIED' | 'DEFERRED' | 'NOT VERIFIED';

const BADGE_CLASS: Record<string, string> = {
  'LIVE VERIFIED': 'live',
  'SOURCE VERIFIED': 'source',
  DEFERRED: 'deferred',
  'NOT VERIFIED': 'absent',
};

function Badge({ kind }: { kind: BadgeKind | string }) {
  return <span className={`badge ${BADGE_CLASS[kind] ?? 'source'}`}>{kind}</span>;
}

interface HeroFile {
  dates: Record<string, string>;
  intervals: { id: string; from: string; to: string; claim_basis: string }[];
  headline_interval_id: string;
  headline_requires_interval_id: string;
  honesty_sentences: string[];
  governing_document: string;
  clinvar_vcv: string;
  qualifying_pmid: string;
  geo_accession: string;
  gene: string;
  variant: string;
}

const hero = historicalCase as unknown as HeroFile;
const run = liveRun as unknown as {
  status: string;
  as_of_utc: string;
  snapshot_source: string;
  binding: Record<string, string>;
  terminal_states: Record<string, number>;
  artifacts: Record<string, number>;
  governance: Record<string, number>;
  duration_seconds: number;
  cloud_run_terminal_state: string;
  manifest_status: string;
  cost_projected_usd_micros: number;
  actual_billed_cost_state: string;
};
const fit = categoryFit as unknown as {
  note: string;
  capabilities: {
    capability: string;
    mechanism: string;
    badge: string;
    evidence: string;
    limit: string;
  }[];
};

function days(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  );
}

function Section({
  num,
  title,
  claim,
  children,
  id,
}: {
  num: string;
  title: string;
  claim: string;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section className="sec" id={id}>
      <div className="sec-head">
        <span className="sec-num">{num}</span>
        <h2>{title}</h2>
      </div>
      <p className="sec-claim">{claim}</p>
      {children}
    </section>
  );
}

/**
 * The evidence chronology, drawn to scale.
 *
 * Marker positions are computed from the dates in the governed case file, so
 * the picture cannot disagree with the numbers printed beside it: the long
 * empty stretch on the right IS the interval the text names.
 */
function EvidenceTimeline() {
  const start = Date.parse(`${hero.dates.geo_public}T00:00:00Z`);
  const paper = Date.parse(`${hero.dates.qualifying_publication}T00:00:00Z`);
  const end = Date.parse(`${hero.dates.clinvar_v5_public}T00:00:00Z`);
  const x = (t: number) => 30 + (700 * (t - start)) / (end - start);
  const marks = [
    { at: x(start), date: hero.dates.geo_public, label: 'Data deposited in public' },
    { at: x(paper), date: hero.dates.qualifying_publication, label: 'Publication indexed' },
    { at: x(end), date: hero.dates.clinvar_v5_public, label: 'Public record updated' },
  ];
  return (
    <figure className="fig timeline">
      <svg viewBox="0 0 760 168" role="img" aria-label="Evidence chronology drawn to scale">
        <g fontFamily="IBM Plex Mono, monospace">
          <line x1="30" y1="66" x2="730" y2="66" stroke="#c9c8c2" />
          {/* the stretch in which the record said the same thing */}
          <line x1={marks[1].at} y1="66" x2="730" y2="66" stroke="#8d3b2f" strokeWidth="2" />
          <text x={(marks[1].at + 730) / 2} y="92" fontSize="11" fill="#8d3b2f" textAnchor="middle">
            {days(hero.dates.qualifying_publication, hero.dates.clinvar_v5_public)} days in which
            the chart did not change
          </text>
          {marks.map((mark, i) => (
            <g key={mark.date}>
              <circle cx={mark.at} cy="66" r="4.5" fill="#fbfbf9" stroke="#15161a" strokeWidth="1.5" />
              <text
                x={mark.at}
                y="40"
                fontSize="11"
                fill="#15161a"
                textAnchor={i === 0 ? 'start' : i === marks.length - 1 ? 'end' : 'middle'}
              >
                {mark.date}
              </text>
              <text
                x={mark.at}
                y="24"
                fontSize="9.5"
                fill="#6a6c73"
                textAnchor={i === 0 ? 'start' : i === marks.length - 1 ? 'end' : 'middle'}
              >
                {mark.label}
              </text>
            </g>
          ))}
          <line x1="30" y1="130" x2="730" y2="130" stroke="#15161a" strokeWidth="1" />
          <text x="380" y="150" fontSize="11" fill="#15161a" textAnchor="middle">
            {days(hero.dates.geo_public, hero.dates.clinvar_v5_public)} days end to end
          </text>
        </g>
      </svg>
      <figcaption className="fig-cap">
        Fig 1. Drawn to scale from the dates above. Nothing failed; the evidence simply
        arrived where nobody was watching.
      </figcaption>
    </figure>
  );
}

const INK = '#15161a';
const RULE = '#c9c8c2';
const MUTED = '#6a6c73';
const MODEL = '#2c4a7c';
const ALERT = '#8d3b2f';

/**
 * A node's mark says what kind of thing it is, and the marks are the whole
 * argument of the drawing: filled squares decide, hollow circles only propose.
 */
type Mark = 'control' | 'model' | 'record';

function Node({
  x,
  y,
  label,
  mark,
  strong = false,
}: {
  x: number;
  y: number;
  label: string;
  mark: Mark;
  strong?: boolean;
}) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {mark === 'control' && <rect x={0} y={-7} width={7} height={7} fill={INK} />}
      {mark === 'model' && (
        <circle cx={3.5} cy={-3.5} r={3.5} fill="none" stroke={MODEL} strokeWidth={1.1} />
      )}
      {mark === 'record' && <rect x={0} y={-4.5} width={7} height={1.6} fill={RULE} />}
      <text
        x={18}
        fontSize={11.5}
        fontWeight={strong ? 600 : 400}
        fill={mark === 'model' ? MODEL : INK}
      >
        {label}
      </text>
    </g>
  );
}

/** A boundary, with its name set into a title tab rather than floating inside it. */
function Zone({
  x,
  y,
  width,
  height,
  tab,
  index,
  name,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  tab: number;
  index: string;
  name: string;
}) {
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill="#ffffff" stroke={RULE} strokeWidth={0.9} />
      <rect x={x} y={y - 20} width={tab} height={20} fill={INK} />
      <text x={x + 12} y={y - 6.5} fontSize={8.5} letterSpacing={1.4} fill="#7f8189">
        {index}
      </text>
      <text x={x + 34} y={y - 6.5} fontSize={8.5} letterSpacing={1.5} fill="#ffffff">
        {name}
      </text>
    </g>
  );
}

/** Two trust boundaries and the human at the end of them. */
function BoundaryDiagram() {
  return (
    <figure className="fig">
      <svg
        viewBox="0 0 980 500"
        role="img"
        aria-label="Three boundaries: the laboratory, Google Cloud, and the specialist who decides"
      >
        <defs>
          <marker id="ar" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto">
            <path d="M0,0 L9,4.5 L0,9 z" fill={INK} />
          </marker>
        </defs>
        <g fontFamily="IBM Plex Mono, monospace" fill={INK}>
          <Zone x={12} y={58} width={284} height={392} tab={116} index="01" name="LABORATORY" />
          <Node x={26} y={100} mark="record" label="Synthetic institutional notes" />
          <Node x={26} y={138} mark="control" label="Deterministic detectors" />
          <Node x={26} y={176} mark="model" label="Local Gemma · residual spans" />
          <Node x={26} y={214} mark="control" label="Adjudication + redaction" />
          <Node x={26} y={252} mark="control" label="Structured-only egress gate" />
          <Node x={26} y={290} mark="record" label="Signed PrivacyReceipt" />
          {/* The loudest claim in the drawing gets the room to be loud. */}
          <rect x={13} y={396} width={282} height={53} fill="#fbf3f1" />
          <line x1={13} y1={396} x2={295} y2={396} stroke="#e8d5d0" strokeWidth={0.9} />
          <text x={154} y={427} fontSize={10} fill={ALERT} textAnchor="middle">
            note text never crosses this line
          </text>

          <Zone x={380} y={58} width={324} height={392} tab={132} index="02" name="GOOGLE CLOUD" />
          <Node x={394} y={100} mark="record" label="Cloud Run Job · Scheduler" />
          <Node x={394} y={132} mark="control" label="Deterministic Controller" strong />
          <Node x={394} y={164} mark="record" label="Agent Registry resolution" />

          {/* The three roles are boxed together because what binds them is a
              shared limit: none of them may decide anything. */}
          <rect x={394} y={186} width={296} height={118} fill="#fcfcfa" stroke={RULE} strokeWidth={0.8} />
          <rect x={394} y={186} width={296} height={18} fill="#f2f1ec" />
          <text x={404} y={199} fontSize={8} letterSpacing={1.3} fill={MUTED}>
            SEPARATED ROLES · MAY PROPOSE ONLY
          </text>
          <Node x={408} y={230} mark="model" label="Evidence Watcher" />
          <Node x={408} y={260} mark="model" label="Evidence Assessor" />
          <Node x={408} y={290} mark="model" label="Citation Auditor" />

          <Node x={394} y={332} mark="control" label="Agent Gateway · receipts" />
          <Node x={394} y={364} mark="record" label="Firestore append-only ledger" />
          <Node x={394} y={396} mark="control" label="Deterministic Policy Gate" strong />
          <Node x={394} y={428} mark="record" label="Observability · traces" />

          <Zone x={784} y={180} width={184} height={150} tab={150} index="03" name="HUMAN AUTHORITY" />
          <Node x={798} y={226} mark="record" label="Simulated ReviewTask" />
          <Node x={798} y={258} mark="record" label="Specialist review" />
          <Node x={798} y={290} mark="control" label="Final decision" strong />

          {/* Labels sit above their arrows, in the gutter, so nothing lands on
              the boxes the way it used to. */}
          <text x={338} y={236} fontSize={7} letterSpacing={0.6} fill={MUTED} textAnchor="middle">
            STRUCTURED ONLY
          </text>
          <line x1={296} y1={254} x2={376} y2={254} stroke={INK} strokeWidth={1} markerEnd="url(#ar)" />
          <line x1={320} y1={245} x2={320} y2={263} stroke={ALERT} strokeWidth={1.5} />
          <text x={320} y={278} fontSize={7} fill={ALERT} textAnchor="middle">
            gate
          </text>

          <text x={744} y={236} fontSize={7} letterSpacing={0.6} fill={MUTED} textAnchor="middle">
            REVIEW ONLY
          </text>
          <line x1={704} y1={254} x2={782} y2={254} stroke={INK} strokeWidth={1} markerEnd="url(#ar)" />

          {/* The key belongs with the drawing, drawn the same way. */}
          <line x1={12} y1={470} x2={968} y2={470} stroke={RULE} strokeWidth={0.8} />
          <g transform="translate(12 490)">
            <rect x={0} y={-7} width={7} height={7} fill={INK} />
            <text x={16} fontSize={9} fill={MUTED}>
              deterministic control · decides
            </text>
          </g>
          <g transform="translate(266 490)">
            <circle cx={3.5} cy={-3.5} r={3.5} fill="none" stroke={MODEL} strokeWidth={1.1} />
            <text x={16} fontSize={9} fill={MUTED}>
              model · proposes only
            </text>
          </g>
          <g transform="translate(478 490)">
            <rect x={0} y={-4.5} width={7} height={1.6} fill={RULE} />
            <text x={16} fontSize={9} fill={MUTED}>
              record or artifact
            </text>
          </g>
          <g transform="translate(664 490)">
            <line x1={3} y1={-9} x2={3} y2={1} stroke={ALERT} strokeWidth={1.5} />
            <text x={16} fontSize={9} fill={MUTED}>
              egress gate · structured payload only
            </text>
          </g>
        </g>
      </svg>
      <figcaption className="fig-cap">
        Fig 2. Three boundaries and a person. The models live inside the cloud boundary and
        may only propose; the deterministic controller and policy gate hold every decision;
        the specialist holds the final one. What leaves the laboratory is a structured,
        minimized, signed payload, never the note.
      </figcaption>
    </figure>
  );
}

export function NarrativePage() {
  const headline = hero.intervals.find((i) => i.id === hero.headline_interval_id)!;
  const required = hero.intervals.find((i) => i.id === hero.headline_requires_interval_id)!;
  const headlineDays = days(hero.dates[headline.from], hero.dates[headline.to]);
  const requiredDays = days(hero.dates[required.from], hero.dates[required.to]);

  return (
    <div className="site">
      <header className="site-head">
        <div className="site-wrap">
          <div className="head-row">
            <span className="head-mark">RECALL</span>
            <nav className="head-links">
              <a href="#how">How it works</a>
              <a href="#fleet">The fleet</a>
              <a href="#run">Live run</a>
              <a href="#platform">Platform mapping</a>
              <a href="#/demo">Evidence surface</a>
            </nav>
          </div>
          <div className="head-flags">
            <span>NON-CLINICAL RESEARCH PROTOTYPE</span>
            <span>SYNTHETIC INSTITUTIONAL RECORDS</span>
            <span>CAPTURED PUBLIC EVIDENCE</span>
          </div>
        </div>
      </header>

      <div className="site-wrap">
        <section className="hero">
          <p className="hero-eyebrow">FORTIFIED ENTERPRISE FLEET</p>
          <h1>
            A zero-trust institutional agent fleet that continuously audits changing genomic
            evidence without allowing any model to become the scientific authority.
          </h1>
          <p>
            When a genetic test returns “uncertain”, the case is filed and almost never
            reopened. Evidence that would change it keeps arriving in public, years later,
            in a different system, with nobody watching. Recall watches.
          </p>
          <p>
            It runs a fleet of agents with separated duties across Google Cloud: one finds new
            evidence, a second judges whether it is material, a third independently re-opens
            every source and verifies the citations. None of them decides. A deterministic
            policy gate reads their signed receipts and rules, and only a human specialist may
            act on the result.
          </p>
          <div className="hero-cta">
            <a className="btn primary" href="#/demo">
              Open the evidence surface →
            </a>
            <a className="btn" href="#how">
              How it works ↓
            </a>
          </div>
        </section>

        <Section
          num="01"
          title="THE PROBLEM"
          claim="Clinical genetics has an alert system. It fires when the paperwork changes, not when the evidence does."
        >
          <p>
            You monitor dependencies for CVEs. Now imagine alerts only fired when the vendor
            updated the changelog, not when the exploit went public. Clinical genetics works
            that way today: the tools watch the changelog.
          </p>
          <p>
            A cancer patient&rsquo;s genetic test comes back <b>“uncertain significance”</b>, a
            classification that means <i>do not act, wait for evidence</i>. It cannot guide
            screening, cannot guide prevention, and cannot be used to test her relatives. That
            one label can stand between her and a drug approved for exactly her kind of tumour.
          </p>
          <p>
            The evidence that would settle it does arrive, years later, in a public database,
            with no connection to the chart it should change.
          </p>

          <table className="tbl card">
            <caption>One real variant, and the sources for every date below.</caption>
            <tbody>
              <tr>
                <td className="cap">Variant</td>
                <td>
                  {hero.gene} <code>{hero.variant}</code>
                </td>
              </tr>
              <tr>
                <td className="cap">ClinVar</td>
                <td>
                  <code>{hero.clinvar_vcv}</code>
                </td>
              </tr>
              <tr>
                <td className="cap">Functional data public</td>
                <td>
                  GEO <code>{hero.geo_accession}</code>, {hero.dates.geo_public}
                </td>
              </tr>
              <tr>
                <td className="cap">Paper published</td>
                <td>
                  PMID <code>{hero.qualifying_pmid}</code>, {hero.dates.qualifying_publication}
                </td>
              </tr>
              <tr>
                <td className="cap">ClinVar first reflection</td>
                <td>
                  {hero.dates.clinvar_v5_public}, <code>{hero.clinvar_vcv}.5</code>
                </td>
              </tr>
            </tbody>
          </table>

          <p className="punch">
            Laboratory evidence that this variant behaves like the harmful ones went public in
            September 2024. The clinical record first moved in April 2026.{' '}
            <b>{headlineDays} days</b>, and in between, nothing was watching.
          </p>

          <EvidenceTimeline />

          <div className="gap-line">
            <div className="gap-item">
              <span className="gap-days">{headlineDays} days</span>
              <span className="gap-basis">{headline.claim_basis}</span>
            </div>
            <div className="gap-item">
              <span className="gap-days">{requiredDays} days</span>
              <span className="gap-basis">{required.claim_basis}</span>
            </div>
          </div>

          <p className="caveat">
            Two intervals, two meanings, never one counter: {headlineDays} from the deposit,{' '}
            {requiredDays} from the publication. {hero.honesty_sentences.join(' ')} Case
            chronology, not a product metric, computed at render from{' '}
            <code>{hero.governing_document}</code>.
          </p>
        </Section>

        <Section
          id="how"
          num="02"
          title="HOW RECALL WORKS"
          claim="Models propose. Deterministic code decides. The boundary between those two sentences is the product."
        >
          <div className="steps">
            {[
              [
                'Watch',
                'A machine-triggered scan opens for a durable case. Public sources are fetched and stored as bytes with their hashes.',
                'The schedule is not a person clicking; the execution creator is the scheduler’s service account.',
              ],
              [
                'Assess',
                'The Evidence Assessor judges whether the change is material for this specific case and proposes a candidate delta.',
                'It may not touch controller-owned identities, and its prose cannot route a case by itself.',
              ],
              [
                'Audit',
                'The Citation Auditor independently re-opens every source and checks each claim against the captured bytes.',
                'The agent that proposed the change is never the agent that verifies it.',
              ],
              [
                'Decide',
                'The deterministic policy gate reads the signed receipts and emits exactly one outcome, with reason codes.',
                'No model output becomes an action. No decision is taken on unverified evidence.',
              ],
              [
                'Hand over',
                'A review-eligible case becomes one simulated task for a human specialist, with its evidence attached.',
                'Recall never changes a clinical record, never reclassifies a variant, and never reaches a patient.',
              ],
            ].map(([what, detail, never], i) => (
              <div className="step" key={what}>
                <span className="step-num">{String(i + 1).padStart(2, '0')}</span>
                <span>
                  <span className="step-what">{what}</span>
                  <span className="step-detail">{detail}</span>
                  <span className="step-never">, {never}</span>
                </span>
              </div>
            ))}
          </div>
          <BoundaryDiagram />
        </Section>

        <Section
          id="fleet"
          num="03"
          title="THE FLEET"
          claim="Three agents with separated duties, one deterministic controller, one gate. The separation is enforced by identity and capability, not by prompt wording."
        >
          <table className="tbl">
            <thead>
              <tr>
                <th>Role</th>
                <th>What it may do</th>
                <th>What it may never do</th>
              </tr>
            </thead>
            <tbody>
              {[
                [
                  'Evidence Watcher',
                  'Find new public evidence and produce an evidence snapshot.',
                  'Bind its own source cursors or identities; the controller owns those.',
                ],
                [
                  'Evidence Assessor',
                  'Judge materiality and propose a candidate delta for one case.',
                  'Alter controller-owned identities, or decide the outcome.',
                ],
                [
                  'Citation Auditor',
                  'Independently re-open every cited source and verify each claim.',
                  'Audit its own proposal; it never wrote one.',
                ],
                [
                  'Deterministic Controller',
                  'Drive lifecycle, enforce tool authority, emit typed artifacts.',
                  'Delegate a decision to a model.',
                ],
                [
                  'Deterministic Policy Gate',
                  'Emit exactly one of NO_ACTION, ABSTAIN or REVIEW_REQUIRED with reason codes.',
                  'Accept model prose as a fact, or act on incomplete proof.',
                ],
              ].map(([role, may, never]) => (
                <tr key={role}>
                  <td className="cap">{role}</td>
                  <td>{may}</td>
                  <td>{never}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            The agents hold no write access to the ledger at all. Each role runs under its own
            service identity with a role-scoped capability, and every tool call passes an
            authorization gate that emits a receipt: without a receipt there is no backend
            authority. In the run described below, all {run.governance.tool_calls} tool calls
            carry an authorization decision and a trace, and{' '}
            <b>{run.governance.unauthorized_calls}</b> were unauthorized. The roles that did
            fail failed loudly: {run.terminal_states.HALTED} cases stopped with a typed receipt
            naming the role and the stage, rather than passing an unfinished result forward.
          </p>
        </Section>

        <Section
          num="04"
          title="THE AUTHORITY BOUNDARY"
          claim="A scan can end in exactly three ways, and a fourth state exists only to say the machinery itself could not be trusted."
        >
          <table className="tbl">
            <thead>
              <tr>
                <th>Outcome</th>
                <th>Meaning</th>
                <th>Consequence</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="cap">NO_ACTION</td>
                <td>No eligible audited change was found for this scan.</td>
                <td>The next scan is scheduled. No task.</td>
              </tr>
              <tr>
                <td className="cap">ABSTAIN</td>
                <td>
                  Recall stopped because required proof was incomplete: a needed fact was
                  missing, invalid, failed or conflicted.
                </td>
                <td>An operations receipt. No task.</td>
              </tr>
              <tr>
                <td className="cap">REVIEW_REQUIRED</td>
                <td>
                  A material change is complete, independently audited and conflict-free.
                </td>
                <td>Exactly one simulated review task for a specialist.</td>
              </tr>
              <tr>
                <td className="cap">HALTED</td>
                <td>
                  A technical terminal: the policy gate or the ledger’s integrity was
                  unavailable, so no trustworthy decision was possible.
                </td>
                <td>
                  Never a task, and never presented as a semantic or clinical outcome.
                </td>
              </tr>
            </tbody>
          </table>
          <p className="caveat">
            HALTED is not a quiet ABSTAIN. Collapsing the two would hide an infrastructure
            failure behind a scientific-sounding word, so the surfaces keep them visually and
            textually distinct; you can see all four states, including the two failure
            endings, on the <a href="#/demo">evidence surface</a>.
          </p>
        </Section>

        <Section
          id="run"
          num="05"
          title="THE RUN THAT HAPPENED"
          claim="Not a diagram. A long-running Cloud Run Job took the whole cohort through the fleet, unattended, and every figure below comes from its own artifacts."
        >
          <span className="stamp">
            COMPLETED · generation 27 · {run.duration_seconds ? `${Math.floor(run.duration_seconds / 3600)}h ${Math.round((run.duration_seconds % 3600) / 60)}m` : ''} unattended · finished{' '}
            {run.as_of_utc}
          </span>
          <div className="grid">
            <div className="cell">
              <span className="cell-value">{run.terminal_states.NO_ACTION}</span>
              <span className="cell-label">cases with nothing to raise</span>
            </div>
            <div className="cell">
              <span className="cell-value">{run.terminal_states.ABSTAIN}</span>
              <span className="cell-label">refused to decide on incomplete proof</span>
            </div>
            <div className="cell">
              <span className="cell-value">{run.terminal_states.HALTED}</span>
              <span className="cell-label">stopped rather than guessed</span>
            </div>
            <div className="cell">
              <span className="cell-value">{run.artifacts.documents.toLocaleString('en-US')}</span>
              <span className="cell-label">
                artifacts, {run.artifacts.parse_failures} parse failures
              </span>
            </div>
            <div className="cell">
              <span className="cell-value">
                {run.governance.authorizations}/{run.governance.tool_calls}
              </span>
              <span className="cell-label">
                tool calls authorized · {run.governance.unauthorized_calls} unauthorized
              </span>
            </div>
            <div className="cell">
              <span className="cell-value">{run.governance.trace_chains}</span>
              <span className="cell-label">trace chains, one per case</span>
            </div>
          </div>
          <p className="kv">
            <b>source commit</b> {run.binding.source_commit}
          </p>
          <p className="kv">
            <b>image digest</b> {run.binding.image_digest}
          </p>
          <p>
            Three of those cases are the whole argument in miniature. A model produced a
            material claim, the Citation Auditor could not verify its sources, and the gate
            emitted ABSTAIN with the reason codes attached; no task, no downstream action, no
            invented certainty. Eight more met an agent timeout and stopped at a technical
            terminal with a typed receipt rather than taking the cohort down with them.
          </p>
          <p className="caveat">
            Cloud Run reports <b>{run.cloud_run_terminal_state}</b> and the cohort day manifest
            reports <b>{run.manifest_status}</b>. Both are true and both are shown: the
            infrastructure finished, and eight cases inside it never reached a decision. The
            run also absorbed {run.governance.http_429} rate-limit responses without a single
            case failing because of one. Projected cost for the cohort is $
            {(Number(run.cost_projected_usd_micros) / 1_000_000).toFixed(2)} against a pinned
            pricing policy; its own <code>actual_billed_cost_state</code> reads{' '}
            {run.actual_billed_cost_state}, so it is a projection and is labelled as one.{' '}
            <a href="#/demo/">Open the run, case by case →</a>
          </p>
        </Section>

        <Section
          id="platform"
          num="06"
          title="PLATFORM CAPABILITY MAPPING"
          claim="Every capability the category asks for, the mechanism that provides it, and exactly how far that claim has been proven."
        >
          <table className="tbl">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Mechanism in Recall</th>
                <th>Verification</th>
              </tr>
            </thead>
            <tbody>
              {fit.capabilities.map((row) => (
                <tr key={row.capability}>
                  <td className="cap">{row.capability}</td>
                  <td>
                    {row.mechanism}
                    <span className="limit">
                      <code>{row.evidence}</code>
                    </span>
                    <span className="limit">{row.limit}</span>
                  </td>
                  <td>
                    <Badge kind={row.badge} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="caveat">{fit.note}</p>

          <h3 style={{ fontSize: '0.86rem', margin: '30px 0 6px' }}>
            How a second department would find these agents
          </h3>
          <p>
            Discovery is a first-class contract, not a README. A consuming team asks the
            registry for a <b>capability</b>, not for a URL, and resolution returns the binding
            together with the mode it was found by: <code>REGISTERED</code> when the agent
            reached the catalog through <code>agents.create</code>,{' '}
            <code>MANUAL_SERVICE</code> when it was registered as a service, and{' '}
            <code>PINNED_FALLBACK</code> when an endpoint was pinned outside the catalog. A
            pinned endpoint is never presented as catalogued. The resolution mode travels as a
            first-class payload field, the catalog record is digest-addressed so a changed
            catalog is detectable, and a capability with no catalog entry is recorded as
            unresolved rather than quietly substituted.
          </p>
        </Section>

        <Section
          num="07"
          title="WHAT NEVER LEAVES THE LABORATORY"
          claim="The clinical note stays home. What travels is a structured, minimized, hash-bound and signed payload."
        >
          <div className="boundary">
            <div className="bx lab">
              <span className="bx-title">STAYS IN THE LABORATORY</span>
              <ul>
                <li>clinical note text</li>
                <li>direct identifiers</li>
                <li>anything not on the egress allowlist</li>
              </ul>
            </div>
            <span className="bx-arrow">→</span>
            <div className="bx out">
              <span className="bx-title">LEAVES, AND ONLY THIS</span>
              <ul>
                <li>declared structured fields</li>
                <li>a pseudonymous case reference</li>
                <li>a signed PrivacyReceipt bound to the payload hash</li>
              </ul>
            </div>
          </div>
          <p>
            Deterministic detectors screen every record first; a local Gemma then proposes the
            residual spans they may have missed, and deterministic adjudication, not the model
, decides what is redacted. The model proposes here too.
          </p>
          <p className="caveat">
            <Badge kind="SOURCE VERIFIED" /> <b>Frozen measurement.</b> The full-cohort privacy
            run of 462 cases was executed once, at measurement commit{' '}
            <code>697aa6eb</code>, and is preserved byte-for-byte. It has been revalidated
            read-only against the current product commit <code>63437d20</code>: 462 of 462
            receipts parsed and verified through the production loader, zero writes, zero
            semantic drift, original bytes unchanged. It is a historical frozen measurement,
            not a measurement of the current run, and it is never re-run: the preregistration
            binds it to a single execution. Separately, the frozen P1 privacy study covers{' '}
            <b>180</b> records; a different population from the 462, never combined with it,
            and its public figures come from the corrected view and the committed erratum.
          </p>
        </Section>

        <Section
          num="08"
          title="PROVENANCE AND LIMITS"
          claim="Everything on this page is either captured public evidence or synthetic institutional data, and the difference is always stated."
        >
          <ul className="bullets">
            <li>
              Every patient-facing record in this project is synthetic. No real person,
              institution record or contact detail exists anywhere in the system.
            </li>
            <li>
              The public evidence, registry pages, the dataset accession, the publication
              record, is captured as bytes and hashed, so a claim can be checked against the
              exact source that was read.
            </li>
            <li>
              This is a non-clinical research prototype. It does not diagnose, does not
              reclassify variants, does not change clinical records, and does not reach
              patients.
            </li>
            <li>
              Run counts and case counts are different things: a case re-evaluated in a later
              epoch is the same case again, labelled, and totals are never summed across
              epochs into one headline.
            </li>
            <li>
              Where a capability is not proven, this page says so. Badges are claims about
              evidence, not about ambition.
            </li>
          </ul>
        </Section>

        <footer className="site-foot">
          <p className="sec-claim">
            The evidence surface is open: real derived records, the three-valued outcomes
            including both failure endings, the cohort ledger and the privacy desk.
          </p>
          <div className="foot-links">
            <a className="btn primary" href="#/demo">
              Open the evidence surface →
            </a>
            <a className="btn" href="#/demo/walkthrough">
              Six-scene walkthrough
            </a>
          </div>
          <p className="foot-note">
            Recall, non-clinical research prototype. Synthetic institutional records and
            captured public evidence. Models propose; deterministic policy decides; a human
            specialist holds the final authority.
          </p>
        </footer>
      </div>
    </div>
  );
}
